import {
  hostedChatSessionMetadataGetForWorkspace,
  hostedChatSessionMetadataSetDisplayName,
  type HostedChatSessionMetadataProcedures,
} from '@owlbert/core'
import { webLog } from '../logging/webLog'

/** Titles we treat as "not yet named" and are safe to overwrite automatically. */
const DEFAULT_TITLE_PATTERNS = [
  /^new chat$/i,
  /^new conversation$/i,
  /^untitled$/i,
  /^chat$/i,
]

/** True when a session still has a placeholder title and may be auto-renamed. */
export function isDefaultChatTitle(name: string | null | undefined): boolean {
  const t = (name ?? '').trim()
  if (!t) return true
  return DEFAULT_TITLE_PATTERNS.some((re) => re.test(t))
}

type TitleMessage = { role: 'user' | 'assistant' | 'system'; content: string }

/** Minimal slice of the LLM client used for title generation. */
interface AutoTitleLlmClient {
  chatAuto: {
    mutate: (input: {
      messages: TitleMessage[]
      system_prompt?: string
      temperature?: number
    }) => Promise<{ content?: string | null }>
  }
}

/** Best-effort title derived directly from the first user message (no LLM). */
function deterministicTitleFromText(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  if (!flat) return ''
  const words = flat.split(' ').slice(0, 8).join(' ')
  return words.length > 60 ? `${words.slice(0, 57).trimEnd()}…` : words
}

function sanitizeTitle(raw: string): string {
  return raw
    .replace(/^["'`\s]+|["'`\s.]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

/**
 * Background helper: if a chat session still has a placeholder title ("New chat",
 * etc.), generate a concise title from the conversation and persist it. Re-reads the
 * freshest title first so we never clobber a name the model (or user) already set.
 * Never throws — failures are logged and swallowed so they can't disrupt the chat flow.
 */
export async function maybeAutoTitleWebChatSession(params: {
  llm: AutoTitleLlmClient
  chatSessionMetadata: HostedChatSessionMetadataProcedures
  workspaceId: string
  sessionId: string
  currentDisplayName?: string | null
  conversation: TitleMessage[]
}): Promise<boolean> {
  const { llm, chatSessionMetadata, workspaceId, sessionId } = params
  try {
    // Re-read in case the model renamed the thread mid-turn via set_chat_display_name.
    let currentName = params.currentDisplayName ?? null
    try {
      const meta = await hostedChatSessionMetadataGetForWorkspace(chatSessionMetadata, { workspaceId })
      const row = (meta as { sessionId: string; displayName: string | null }[]).find(
        (r) => r.sessionId === sessionId,
      )
      if (row) currentName = row.displayName
    } catch {
      // Fall back to the caller-provided name.
    }
    if (!isDefaultChatTitle(currentName)) return false

    const transcript = params.conversation
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(0, 6)
      .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content.slice(0, 800)}`)
      .join('\n')
    const firstUser = params.conversation.find((m) => m.role === 'user')?.content ?? ''
    if (!transcript.trim() && !firstUser.trim()) return false

    let title = ''
    try {
      const reply = await llm.chatAuto.mutate({
        messages: [
          {
            role: 'user',
            content:
              'Generate a short, specific title (about 6 words or fewer, max 60 characters) for the chat below. ' +
              'Reply with ONLY the title text — no surrounding quotes, no trailing punctuation, no markdown.\n\n' +
              transcript,
          },
        ],
        temperature: 0.2,
      })
      title = sanitizeTitle(reply.content ?? '')
    } catch (e) {
      webLog('warn', 'chat-autotitle', 'llm title generation failed; using fallback', {
        message: String(e),
        sessionId,
      })
    }

    if (!title) title = sanitizeTitle(deterministicTitleFromText(firstUser))
    if (!title) return false

    await hostedChatSessionMetadataSetDisplayName(chatSessionMetadata, {
      workspaceId,
      sessionId,
      name: title,
    })
    webLog('info', 'chat-autotitle', 'auto-titled chat session', { sessionId, title })
    return true
  } catch (e) {
    webLog('warn', 'chat-autotitle', 'auto-title failed', { message: String(e), sessionId })
    return false
  }
}
